import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

import arrowLeft from "../../assets/arrow-left.png";
import arrowRight from "../../assets/arrow-right.png";
import close from "../../assets/close.png";

// One page for every overview category. The title is taken from the URL
// (/overview/:titleSlug) and its images come from the backend, so adding a new
// overview title in the admin panel automatically gets a working page.
const Overview = () => {
  const { titleSlug } = useParams();

  const [images, setImages] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const [columns, setColumns] = useState(4);
  const [imageColumns, setImageColumns] = useState([]);

  // Fetch this title's images
  useEffect(() => {
    const fetchImages = async () => {
      try {
        if (!titleSlug) return;
        const response = await fetch(`${process.env.REACT_APP_API_URL}/overview-upload/${titleSlug}`);
        if (!response.ok) {
          setImages([]);
          return;
        }
        const data = await response.json();
        setImages(
          data.map((img) => ({
            src: img.image,
            caption: img.caption || "",
          }))
        );
      } catch (error) {
        console.error("Error fetching overview images:", error);
        setImages([]);
      }
    };

    fetchImages();
  }, [titleSlug]);

  // Responsive column adjustment
  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerWidth;
      if (screenWidth < 640) setColumns(1);
      else if (screenWidth < 768) setColumns(2);
      else if (screenWidth < 1024) setColumns(3);
      else setColumns(4);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Distribute images across columns
  useEffect(() => {
    if (images.length === 0) {
      setImageColumns([]);
      return;
    }

    const newColumns = Array.from({ length: columns }, () => []);
    images.forEach((image, index) => {
      const columnIndex = index % columns;
      newColumns[columnIndex].push({ ...image, index });
    });

    setImageColumns(newColumns);
  }, [images, columns]);

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setCurrentImageIndex(0);
  };

  const showNextImage = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  const showPrevImage = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
  };

  return (
    <>
      <div className="bg-[#252525] min-h-screen py-8 xl:px-[150px] lg:px-[150px] px-[30px]">
        <div className="flex gap-4">
          {imageColumns.map((column, colIndex) => (
            <div key={colIndex} className="flex-1 flex flex-col gap-4">
              {column.map((image, imgIndex) => (
                <div
                  key={imgIndex}
                  className="w-full cursor-pointer"
                  onClick={() => openLightbox(image.index)}
                >
                  <img
                    src={image.src}
                    alt={image.caption || `Gallery ${image.index + 1}`}
                    className="w-full object-cover shadow-md hover:shadow-xl transition-transform duration-300 hover:scale-105"
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && images.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="relative flex items-center justify-center w-full">
            {/* Previous Button */}
            <button
              onClick={showPrevImage}
              className="absolute left-4 text-white text-3xl w-10 h-10 flex items-center justify-center"
            >
              <img width="24" height="24" src={arrowLeft} alt="Previous" />
            </button>

            {/* Image + caption */}
            <div className="relative inline-block max-h-screen">
              <img
                src={images[currentImageIndex].src}
                alt={`Lightbox ${currentImageIndex + 1}`}
                className="block max-w-full max-h-screen"
              />
              {images[currentImageIndex].caption ? (
                <div className="font-dropdown absolute bottom-0 left-0 right-0 bg-black/70 px-5 py-4 text-[#e4e4e3] text-[15px] leading-relaxed">
                  {images[currentImageIndex].caption}
                </div>
              ) : null}
            </div>

            {/* Next Button */}
            <button
              onClick={showNextImage}
              className="absolute right-4 text-white text-3xl w-10 h-10 flex items-center justify-center"
            >
              <img width="24" height="24" src={arrowRight} alt="Next" />
            </button>

            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 text-white text-3xl w-10 h-10 flex items-center justify-center"
            >
              <img width="24" height="24" src={close} alt="Close" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Overview;
